import React, {
  FormEvent,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useUser } from "../contexts/UserContext";
import {
  requestSupportChatBlockStatus,
  requestSupportChatReply,
  SupportChatHistoryItem,
} from "./firebaseFunctions";
import styles from "./SupportChatbot.module.css";

type ChatMessage = {
  id: string;
  role: "user" | "assistant";
  text: string;
};

const MAX_HISTORY_ITEMS = 10;
const MIN_USER_MESSAGE_LENGTH = 2;
const BOT_AVATAR = "\uD83E\uDD16";
const USER_AVATAR = "\uD83D\uDE42";

const createMessageId = (): string =>
  `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const resolveLocaleTag = (locale: string): "ko" | "ja" | "zh" | "en" => {
  const normalized = locale.trim().toLowerCase();
  if (normalized.startsWith("ko")) {
    return "ko";
  }
  if (normalized.startsWith("ja")) {
    return "ja";
  }
  if (normalized.startsWith("zh")) {
    return "zh";
  }
  return "en";
};

const getSystemGreeting = (locale: string): string => {
  const localeTag = resolveLocaleTag(locale);
  if (localeTag === "ko") {
    return "안녕하세요. AUTOVID AI 상담원입니다. 문의 내용을 입력해 주세요.";
  }
  if (localeTag === "ja") {
    return "こんにちは。AUTOVID AIサポートです。お問い合わせ内容を入力してください。";
  }
  if (localeTag === "zh") {
    return "您好，这里是 AUTOVID AI 客服。请输入您要咨询的内容。";
  }
  return "Hello, this is AUTOVID AI Support. Please enter your question.";
};

const getSupportGuidanceText = (locale: string): string => {
  const localeTag = resolveLocaleTag(locale);
  if (localeTag === "ko") {
    return "추가 확인이 필요한 문의입니다. 고객문의 페이지로 접수해 주세요. support@dreamcraft.co.kr 로도 문의할 수 있습니다.";
  }
  if (localeTag === "ja") {
    return "詳細確認が必要な問い合わせです。サポートページから申請してください。support@dreamcraft.co.kr でも受け付けています。";
  }
  if (localeTag === "zh") {
    return "该问题需要人工进一步确认。请通过客户支持页面提交，或发送邮件至 support@dreamcraft.co.kr。";
  }
  return "This inquiry needs manual verification. Please submit through the support page or email support@dreamcraft.co.kr.";
};

const getBlockedMessage = (locale: string): string => {
  const localeTag = resolveLocaleTag(locale);
  if (localeTag === "ko") {
    return "부적절한 언어 또는 반복 문의가 감지되어 AI 상담이 1시간 동안 제한되었습니다. 고객문의 페이지로 접수해 주세요.";
  }
  if (localeTag === "ja") {
    return "不適切な言葉または繰り返しの問い合わせが検出されたため、AI相談は1時間制限されています。サポートページをご利用ください。";
  }
  if (localeTag === "zh") {
    return "检测到不当用语或重复咨询，AI 咨询已被限制 1 小时。请通过客户支持页面提交。";
  }
  return "Inappropriate language or repeated inquiries were detected, so AI chat is limited for 1 hour. Please use the support page.";
};

const parseBlockedUntilMs = (value: string): number | null => {
  if (value.trim().length === 0) {
    return null;
  }
  const parsed = new Date(value);
  if (!Number.isFinite(parsed.getTime())) {
    return null;
  }
  return parsed.getTime();
};

const isBlockActive = (blockedUntilMs: number | null, nowMs: number): boolean => {
  if (blockedUntilMs === null) {
    return false;
  }
  return blockedUntilMs > nowMs;
};

const SupportChatbot: React.FC = () => {
  const { t } = useTranslation();
  const { lang } = useParams<{ lang: string }>();
  const currentLang = lang ?? "en";
  const localeTag = resolveLocaleTag(currentLang);

  const userContext = useUser();
  const userDetails = userContext?.userDetails;
  const setUserDetails = userContext?.setUserDetails;
  const userId = userDetails?.id ?? "Guest";
  const isGuest = userId === "Guest";
  const supportChatBlockedUntil = userDetails?.supportChatBlockedUntil ?? "";
  const chatLogRef = useRef<HTMLDivElement | null>(null);
  const chatBottomRef = useRef<HTMLDivElement | null>(null);

  const greetingText = useMemo(
    () => getSystemGreeting(currentLang),
    [currentLang]
  );
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: createMessageId(),
      role: "assistant",
      text: greetingText,
    },
  ]);
  const [draft, setDraft] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [errorText, setErrorText] = useState("");
  const [needsSupportInquiry, setNeedsSupportInquiry] = useState(false);
  const [serverCheckedUserId, setServerCheckedUserId] = useState("");
  const isBlockedByPolicy = isBlockActive(
    parseBlockedUntilMs(supportChatBlockedUntil),
    Date.now()
  );

  useEffect(() => {
    setMessages((prev) => {
      if (prev.length === 1 && prev[0]?.role === "assistant") {
        return [{ ...prev[0], text: greetingText }];
      }
      return prev;
    });
  }, [greetingText]);

  const scrollChatToBottom = () => {
    const chatLog = chatLogRef.current;
    if (!chatLog) {
      return;
    }
    chatLog.scrollTop = chatLog.scrollHeight;

    chatBottomRef.current?.scrollIntoView({
      block: "end",
      inline: "nearest",
    });
  };

  useLayoutEffect(() => {
    scrollChatToBottom();
    const animationFrameId = window.requestAnimationFrame(scrollChatToBottom);
    const timeoutId = window.setTimeout(scrollChatToBottom, 80);

    return () => {
      window.cancelAnimationFrame(animationFrameId);
      window.clearTimeout(timeoutId);
    };
  }, [messages, isSending]);

  useEffect(() => {
    if (isGuest) {
      setServerCheckedUserId("");
      return;
    }

    const active = isBlockActive(
      parseBlockedUntilMs(supportChatBlockedUntil),
      Date.now()
    );

    if (!active && supportChatBlockedUntil.length > 0 && setUserDetails) {
      setUserDetails((prev) => ({
        ...prev,
        supportChatBlockedUntil: "",
      }));
    }
  }, [isGuest, setUserDetails, supportChatBlockedUntil]);

  useEffect(() => {
    if (isGuest) {
      return;
    }
    if (serverCheckedUserId === userId) {
      return;
    }

    const localActive = isBlockActive(
      parseBlockedUntilMs(supportChatBlockedUntil),
      Date.now()
    );
    if (localActive) {
      setServerCheckedUserId(userId);
      return;
    }

    let cancelled = false;
    setServerCheckedUserId(userId);
    requestSupportChatBlockStatus(userId)
      .then((status) => {
        if (cancelled) {
          return;
        }
        if (!setUserDetails) {
          return;
        }
        const blockedMs = parseBlockedUntilMs(status.blockedUntil);
        const active = isBlockActive(blockedMs, Date.now());
        setUserDetails((prev) => ({
          ...prev,
          supportChatBlockedUntil: active ? status.blockedUntil : "",
        }));
      })
      .catch(() => {
        // Non-blocking check only. Ignore errors and continue with local state.
      });

    return () => {
      cancelled = true;
    };
  }, [isGuest, serverCheckedUserId, setUserDetails, supportChatBlockedUntil, userId]);

  const canSubmit =
    !isSending &&
    draft.trim().length >= MIN_USER_MESSAGE_LENGTH &&
    !isGuest &&
    !isBlockedByPolicy;

  const appendAssistantMessage = (text: string) => {
    setMessages((prev) => [
      ...prev,
      {
        id: createMessageId(),
        role: "assistant",
        text,
      },
    ]);
  };

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = draft.trim();
    if (trimmed.length < MIN_USER_MESSAGE_LENGTH) {
      return;
    }

    if (isGuest) {
      setErrorText(
        t("support_chatbot.sign_in_required", {
          defaultValue: localeTag === "ko" ? "로그인 후 이용해 주세요." : "Please sign in first.",
        })
      );
      return;
    }

    if (isBlockedByPolicy) {
      const blockMessage = getBlockedMessage(currentLang);
      appendAssistantMessage(blockMessage);
      setNeedsSupportInquiry(true);
      setErrorText(blockMessage);
      return;
    }

    try {
      const status = await requestSupportChatBlockStatus(userId);
      const blockedMs = parseBlockedUntilMs(status.blockedUntil);
      const active = isBlockActive(blockedMs, Date.now());
      if (setUserDetails) {
        setUserDetails((prev) => ({
          ...prev,
          supportChatBlockedUntil: active ? status.blockedUntil : "",
        }));
      }
      if (active) {
        const blockMessage = getBlockedMessage(currentLang);
        appendAssistantMessage(blockMessage);
        setNeedsSupportInquiry(true);
        setErrorText(blockMessage);
        return;
      }
    } catch {
      // Non-blocking pre-check. If this fails, backend still enforces the block.
    }

    setErrorText("");
    setDraft("");

    const userMessage: ChatMessage = {
      id: createMessageId(),
      role: "user",
      text: trimmed,
    };

    const historyBeforeSend: SupportChatHistoryItem[] = messages
      .slice(-MAX_HISTORY_ITEMS)
      .map((item) => ({
        role: item.role,
        content: item.text,
      }));

    setMessages((prev) => [...prev, userMessage]);
    setIsSending(true);

    try {
      const result = await requestSupportChatReply(
        userId,
        trimmed,
        currentLang,
        historyBeforeSend
      );

      appendAssistantMessage(result.answer);
      setNeedsSupportInquiry(result.needs_support_inquiry === true);

      if (
        result.response_type === "malicious_repeat" &&
        result.blocked_until
      ) {
        const blockedUntil = result.blocked_until;
        if (setUserDetails) {
          setUserDetails((prev) => ({
            ...prev,
            supportChatBlockedUntil: blockedUntil,
          }));
        }
      }
    } catch (error: any) {
      setErrorText(
        error?.message ??
          t("support_chatbot.error", {
            defaultValue:
              localeTag === "ko"
                ? "AI 상담 응답 중 오류가 발생했습니다."
                : "Failed to receive AI support response.",
          })
      );
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className={styles.page}>
      <section className={styles.hero}>
        <h1>
          {t("support_chatbot.title", {
            defaultValue:
              localeTag === "ko"
                ? "AI 상담 챗봇"
                : localeTag === "ja"
                  ? "AIサポートチャット"
                  : localeTag === "zh"
                    ? "AI 客服聊天"
                    : "AI Support Chatbot",
          })}
        </h1>
        <p className={styles.subtitle}>
          {t("support_chatbot.subtitle", {
            defaultValue:
              localeTag === "ko"
                ? "간단한 문의는 즉시 답변하고, 추가 확인이 필요하면 고객문의로 안내합니다."
                : localeTag === "ja"
                  ? "簡単な質問にはすぐ回答し、詳細確認が必要な場合はサポート申請へ案内します。"
                  : localeTag === "zh"
                    ? "简单问题可立即答复，需人工确认时将引导至客户支持。"
                    : "Get instant AI answers for simple questions, then move to support inquiry when needed.",
          })}
        </p>
      </section>

      {isGuest && (
        <div className={styles.notice}>
          <p>
            {t("support_chatbot.sign_in_required", {
              defaultValue:
                localeTag === "ko"
                  ? "AI 상담은 로그인 회원만 이용할 수 있습니다."
                  : localeTag === "ja"
                    ? "AI相談はログインした会員のみ利用できます。"
                    : localeTag === "zh"
                      ? "AI 咨询仅限登录会员使用。"
                      : "AI support chat is available only for signed-in users.",
            })}
          </p>
          <Link to={`/${currentLang}/login`} className={styles.loginLink}>
            {t("support_chatbot.sign_in", {
              defaultValue:
                localeTag === "ko"
                  ? "로그인"
                  : localeTag === "ja"
                    ? "ログイン"
                    : localeTag === "zh"
                      ? "登录"
                      : "Sign In",
            })}
          </Link>
        </div>
      )}

      {needsSupportInquiry && (
        <div className={styles.supportNotice}>
          <p>{getSupportGuidanceText(currentLang)}</p>
          <Link to={`/${currentLang}/support`} className={styles.supportNoticeLink}>
            {t("support_chatbot.go_support", {
              defaultValue:
                localeTag === "ko"
                  ? "고객문의로 이동"
                  : localeTag === "ja"
                    ? "サポートフォームへ移動"
                    : localeTag === "zh"
                      ? "前往客户支持表单"
                      : "Go to Support Form",
            })}
          </Link>
        </div>
      )}

      <section className={styles.chatCard}>
        <div className={styles.chatLog} ref={chatLogRef}>
          {messages.map((item) => (
            <div
              key={item.id}
              className={
                item.role === "user" ? styles.messageRowUser : styles.messageRowBot
              }
            >
              {item.role === "assistant" && (
                <span className={styles.avatarBot} aria-hidden>
                  {BOT_AVATAR}
                </span>
              )}
              <div
                className={
                  item.role === "user" ? styles.userMessage : styles.botMessage
                }
              >
                {item.text}
              </div>
              {item.role === "user" && (
                <span className={styles.avatarUser} aria-hidden>
                  {USER_AVATAR}
                </span>
              )}
            </div>
          ))}

          {isSending && (
            <div className={styles.messageRowBot}>
              <span className={styles.avatarBot} aria-hidden>
                {BOT_AVATAR}
              </span>
              <div className={styles.botMessage}>
                {t("support_chatbot.thinking", {
                  defaultValue:
                    localeTag === "ko"
                      ? "답변을 준비하고 있습니다..."
                      : localeTag === "ja"
                        ? "回答を準備しています..."
                        : localeTag === "zh"
                          ? "正在准备回答..."
                          : "Thinking...",
                })}
              </div>
            </div>
          )}
          <div
            ref={chatBottomRef}
            className={styles.chatBottomAnchor}
            aria-hidden
          />
        </div>

        <form className={styles.form} onSubmit={onSubmit}>
          <textarea
            className={styles.input}
            rows={4}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder={t("support_chatbot.placeholder", {
              defaultValue:
                localeTag === "ko"
                  ? "문의 내용을 입력해 주세요."
                  : localeTag === "ja"
                    ? "お問い合わせ内容を入力してください。"
                    : localeTag === "zh"
                      ? "请输入咨询内容。"
                      : "Type your support question.",
            })}
            disabled={isSending || isGuest || isBlockedByPolicy}
          />
          {errorText.length > 0 && <p className={styles.errorText}>{errorText}</p>}
          <div className={styles.actions}>
            <Link to={`/${currentLang}/support`} className={styles.secondaryButton}>
              {t("support_chatbot.go_support", {
                defaultValue:
                  localeTag === "ko"
                    ? "고객문의로 이동"
                    : localeTag === "ja"
                      ? "サポートフォームへ移動"
                      : localeTag === "zh"
                        ? "前往客户支持表单"
                        : "Go to Support Form",
              })}
            </Link>
            <button
              type="submit"
              className={styles.primaryButton}
              disabled={!canSubmit}
            >
              {isSending
                ? t("support_chatbot.sending", {
                    defaultValue:
                      localeTag === "ko"
                        ? "전송 중..."
                        : localeTag === "ja"
                          ? "送信中..."
                          : localeTag === "zh"
                            ? "发送中..."
                            : "Sending...",
                  })
                : t("support_chatbot.send", {
                    defaultValue:
                      localeTag === "ko"
                        ? "보내기"
                        : localeTag === "ja"
                          ? "送信"
                          : localeTag === "zh"
                            ? "发送"
                            : "Send",
                  })}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
};

export default SupportChatbot;
